// Libs
import classNames from 'classnames/bind';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { TFunction } from 'i18next';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, FormGroup, InputLabel, Stack, Typography } from '@mui/material';
// Components, Layouts, Pages
import {
  BaseFilters,
  BaseInputSearch,
  BaseSelect,
  BaseTable,
  BaseTableOption,
  BaseToolbar,
  ConfirmModal,
  Loading,
} from '~/components/common';
import PageShell from '~/pages/common/pageShell/PageShell';
// Others
import useDebounce from '~/utils/hooks/useDebounce';
import { IBaseOption, IFilterValueChange, IHeaderCellType } from '~/utils/interface/common';
import { ICampaign, IQueryParamCampaign } from '~/utils/interface/campaign';
import {
  BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
  DEFAULT_DEBOUNCE_DELAY_TIME,
  DEFAULT_LIMIT_PAGE,
  DEFAULT_NUMBER_ZERO,
  EMPTY_STRING,
  FILTER_DEFAULT_VALUES,
} from '~/utils/constants/common';
import { useAppDispatch } from '~/utils/redux/hooks';
import { StorageEnum } from '~/utils/enum/base';
import { IPaginationResponse } from '~/utils/interface/base';
import { formatTitleCase } from '~/utils/helper/common';
import { RoleEnum, SearchByFilterContentEnum } from '~/utils/enum/common';
import { mockDataCampaignType } from '~/utils/mockData/campaign';
import { adminRoute, adminRouteAbsolute } from '~/utils/routes/admin';
import { staffRoute, staffRouteAbsolute } from '~/utils/routes/staff';
import { deleteCampaign, getListCampaign } from '~/thunks/campaign/campaignThunk';
import { campaignActions, selectIsRefreshCampaignList } from '~/thunks/campaign/campaignSlice';
// Styles, images, icons
import styles from './Campaign.module.scss';
import { icons } from '~/assets';

type Props = {};

const cx = classNames.bind(styles);

const columns = (
  t: TFunction,
  onView: (id: string) => void,
  onEdit: (id: string) => void,
  onDelete: (id: string) => void
): IHeaderCellType<ICampaign>[] => {
  return [
    {
      id: 'name',
      label: t('table_campaign_title_name'),
      render: (record: ICampaign) => record?.name || EMPTY_STRING,
      minWidth: 150,
    },
    {
      id: 'type',
      label: t('table_campaign_title_type'),
      render: (record: ICampaign) => {
        const unit: IBaseOption = mockDataCampaignType.find(
          (item) => item.value === record?.type
        ) as IBaseOption;

        return unit ? unit?.label : EMPTY_STRING;
      },
    },
    {
      id: 'action',
      label: t('table_campaign_title_action'),
      align: 'left',
      render: (record: ICampaign) => {
        return (
          <BaseTableOption
            onView={() => onView(record.id)}
            onEdit={() => onEdit(record.id)}
            onDelete={() => onDelete(record.id)}
          />
        );
      },
    },
  ];
};

const Campaign = (props: Props) => {
  //#region Destructuring Props

  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/campaign']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => Object.fromEntries([...searchParams]), [searchParams]);
  const pageSelected = useMemo<number>(
    () => Number(params?.page ?? BASE_PAGINATION_DEFAULT_CURRENT_PAGE),
    [params?.page]
  );
  const limitPage = useMemo<number>(() => Number(params?.limit ?? DEFAULT_LIMIT_PAGE), [params?.limit]);
  const textSearchParams = useMemo<string>(
    () => String(params?.textSearch || EMPTY_STRING),
    [params?.textSearch]
  );

  const currentRole: string = localStorage.getItem(StorageEnum.ROLE) ?? EMPTY_STRING;
  //#endregion Declare Hook

  //#region Selector
  const isRefreshCampaignList = useSelector(selectIsRefreshCampaignList);
  //#endregion Selector

  //#region Declare State
  const [filterParams, setFilterParams] = useState<IQueryParamCampaign>(FILTER_DEFAULT_VALUES);
  const [paramObject, setParamObject] = useState<IQueryParamCampaign>({
    page: pageSelected,
    limit: limitPage,
  });
  const [pagination, setPagination] = useState<IPaginationResponse>();

  const [searchPlaceholder, setSearchPlaceholder] = useState<string>(EMPTY_STRING);
  const [searchKey, setSearchKey] = useState<string>(EMPTY_STRING);
  const debounceSearchKey = useDebounce(searchKey, DEFAULT_DEBOUNCE_DELAY_TIME);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [totalItems, setTotalItems] = useState<string>();
  const [campaignList, setCampaignList] = useState<ICampaign[]>([]);

  const [campaignSelected, setCampaignSelected] = useState<string | null>(null);
  const [isShowConfirmDelete, setIsShowConfirmDelete] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetCampaignList(paramObject);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramObject]);

  useEffect(() => {
    if (!isRefreshCampaignList) return;

    handleGetCampaignList(paramObject);
    dispatch(campaignActions.setRefreshCampaignList(false));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefreshCampaignList]);

  useEffect(() => {
    setSearchKey(textSearchParams);
  }, [textSearchParams]);

  useEffect(() => {
    if (Array(filterParams.fields)) {
      const placeholder = filterParams.fields?.map((item) => formatTitleCase(item))?.join(', ');
      setSearchPlaceholder(placeholder as string);
    }
  }, [filterParams.fields]);

  useEffect(() => {
    if (pagination || debounceSearchKey) {
      setParamObject({
        ...paramObject,
        page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
        limit: limitPage,
        ...(debounceSearchKey ? { textSearch: debounceSearchKey } : { textSearch: undefined }),
        ...(filterParams ? { ...filterParams } : {}),
      });

      setSearchParams({
        page: String(BASE_PAGINATION_DEFAULT_CURRENT_PAGE),
        limit: String(limitPage),
        ...(searchKey ? { textSearch: debounceSearchKey } : {}),
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounceSearchKey]);

  useEffect(() => {
    if (!searchKey) return;

    setParamObject({
      ...paramObject,
      page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
      limit: limitPage,
      ...(filterParams ? { ...filterParams } : {}),
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterParams]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetCampaignList = (params: IQueryParamCampaign) => {
    setIsLoading(true);

    dispatch(getListCampaign(params))
      .unwrap()
      .then((res) => {
        const { responses, pagination, totalItems } = res?.data;

        if (!responses) return;

        setCampaignList(responses);
        setPagination(pagination);
        setTotalItems(totalItems.toString());
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleAddCampaign = () => {
    switch (currentRole) {
      case RoleEnum.ADMIN:
        navigate(adminRouteAbsolute.createCampaign);
        break;

      case RoleEnum.STAFF:
        navigate(staffRouteAbsolute.createCampaign);
        break;
    }
  };

  const handleEditCampaign = (id: string) => {
    switch (currentRole) {
      case RoleEnum.ADMIN:
        navigate(adminRouteAbsolute.updateCampaign(id));
        break;

      case RoleEnum.STAFF:
        navigate(staffRouteAbsolute.updateCampaign(id));
        break;
    }
  };

  const handleDeleteCampaign = (id: string) => {
    if (!id) return;

    setCampaignSelected(id);
    setIsShowConfirmDelete(true);
  };

  const handleConfirmDelete = () => {
    if (!campaignSelected) return;

    setIsLoading(true);

    dispatch(deleteCampaign(campaignSelected))
      .unwrap()
      .then((_res) => {
        dispatch(campaignActions.setRefreshCampaignList(true));
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
        handleCloseConfirmDelete();
      });
  };

  const handleCloseConfirmDelete = () => {
    setIsShowConfirmDelete(false);
    setCampaignSelected(null);
  };

  const handleViewCampaign = (id: string) => {
    switch (currentRole) {
      case RoleEnum.ADMIN:
        navigate(`${adminRoute.base}${adminRoute.marketing}${adminRoute.campaigns}/${id}`);
        break;

      case RoleEnum.STAFF:
        navigate(`${staffRoute.base}${staffRoute.marketing}${staffRoute.campaigns}/${id}`);
        break;
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchKey(value);
  };

  const handleApplyFilter = (values: IQueryParamCampaign | undefined) => {
    if (!values) {
      setFilterParams({});
      return;
    }

    setFilterParams({
      ...values,
    });

    setParamObject({
      ...paramObject,
      ...values,
    });
  };

  const handlePageChange = (_event: ChangeEvent<unknown>, page: number) => {
    setParamObject({ ...paramObject, page });
    setSearchParams({ ...params, page: String(page), limit: String(limitPage) });
  };

  const handleRowPerPageChange = (rowPerPage: number) => {
    setParamObject({ ...paramObject, page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE, limit: rowPerPage });
    setSearchParams({
      ...params,
      page: String(BASE_PAGINATION_DEFAULT_CURRENT_PAGE),
      limit: String(rowPerPage),
    });
  };

  const handleFilterUnitChange = (
    option: IBaseOption,
    onChange?: (value: IFilterValueChange<IQueryParamCampaign>) => void
  ) => {
    if (!option) return;

    const { value } = option;

    onChange?.({ name: 'type', value: value });
  };

  //#endregion Handle Function

  return (
    <PageShell title={t('menu_campaigns', { ns: 'common/sidebar' })}>
    <Box component={'div'} id='campaignPage' className={cx('container')}>
      <BaseToolbar
        title={t('campaign_title_total', { quantity: totalItems })}
        textAction={t('button_add_campaign')}
        onAction={handleAddCampaign}
      >
        <BaseInputSearch
          searchByContent={
            searchPlaceholder
              ? `${SearchByFilterContentEnum.NAME}, ${searchPlaceholder}`
              : SearchByFilterContentEnum.NAME
          }
          value={searchKey}
          onChange={handleSearchChange}
        />

        <BaseFilters<IQueryParamCampaign>
          value={filterParams}
          defaultValue={FILTER_DEFAULT_VALUES}
          onApply={handleApplyFilter}
        >
          {({ value, onChange }) => (
            <Stack gap={'12px'}>
              <Typography className={cx('title')}>
                {t('common_action_filter_by', { ns: 'common/base' })}
              </Typography>

              <FormGroup>
                <Box component={'div'} className={cx('filterGroup')}>
                  <InputLabel className={cx('label')}>{t('filter_label_type')}</InputLabel>

                  <BaseSelect
                    layout='horizontal'
                    width={'160px'}
                    options={mockDataCampaignType}
                    value={value?.type}
                    onChange={(option) => handleFilterUnitChange(option, onChange)}
                  />
                </Box>
              </FormGroup>
            </Stack>
          )}
        </BaseFilters>
      </BaseToolbar>

      <Box component={'div'} className={cx('body')}>
        <BaseTable
          currentRowsPerPage={limitPage}
          currentPage={pageSelected}
          count={pagination?.totalPages || DEFAULT_NUMBER_ZERO}
          columns={columns(t, handleViewCampaign, handleEditCampaign, handleDeleteCampaign)}
          dataSource={campaignList || []}
          onPageChange={handlePageChange}
          onRowClick={handleViewCampaign}
          onRowsPerPageChange={handleRowPerPageChange}
        />
      </Box>

      {isShowConfirmDelete && (
        <ConfirmModal
          type='delete'
          icon={icons.tableIconActionDelete}
          title={t('common_title_confirm_delete', { ns: 'common/base' })}
          textConfirm={t('common_action_delete', { ns: 'common/base' })}
          onClose={handleCloseConfirmDelete}
          onConfirm={handleConfirmDelete}
        />
      )}

      {isLoading && <Loading />}
    </Box>
    </PageShell>
  );
};

export default Campaign;
